import apiClient from "../api-client.js";

/**
 * Google integrations endpoints.
 *
 * Two levels, the same shape as the connect dialog:
 *   - a Google account is authorized once per agency and reused by everyone in it
 *   - each client points at one GA4 property of that account
 *
 * Only those links live in the database. Every metric in getAnalyticsReport is
 * queried live from Google on each call, so there is nothing to cache here.
 */

const BASE = "/integrations/google";

/** Google accounts already authorized for the current user's agency. */
export const listGoogleAccounts = async () => {
  const response = await apiClient.get(`${BASE}/accounts/`);
  return response.data;
};

/**
 * Start the OAuth flow.
 *
 * Returns the URL the consultant has to visit. The account only exists once
 * Google redirects back to the backend callback.
 */
export const startGoogleAuthorization = async () => {
  const response = await apiClient.post(`${BASE}/accounts/authorize/`);
  return response.data.authorization_url;
};

/** Disconnect an account and revoke the grant at Google. */
export const deleteGoogleAccount = async (accountId) => {
  await apiClient.delete(`${BASE}/accounts/${accountId}/`);
};

/**
 * GA accounts and GA4 properties this Google account can read.
 *
 * Fills the second and third dropdowns. Google already filters by permission,
 * so whatever comes back is what the account is allowed to see.
 */
export const listAnalyticsProperties = async (accountId) => {
  const response = await apiClient.get(
    `${BASE}/accounts/${accountId}/analytics-properties/`
  );
  return response.data.accounts;
};

/** The property linked to a client, or null when it has never been connected. */
export const getClientAnalyticsLink = async (clientId) => {
  const response = await apiClient.get(`${BASE}/analytics/`, {
    params: { client: clientId },
  });
  return response.data[0] || null;
};

/** Link a property to a client, replacing whatever it had. */
export const linkAnalyticsProperty = async (payload) => {
  const response = await apiClient.post(`${BASE}/analytics/`, payload);
  return response.data;
};

export const unlinkAnalyticsProperty = async (linkId) => {
  await apiClient.delete(`${BASE}/analytics/${linkId}/`);
};

/**
 * The whole GA4 dashboard for a linked client.
 *
 * One backend call becomes one batched request to Google, so asking for the
 * report is cheap; asking for it in pieces would not be.
 */
export const getAnalyticsReport = async (
  linkId,
  { startDate, endDate, compareStartDate, compareEndDate } = {}
) => {
  const response = await apiClient.get(`${BASE}/analytics/${linkId}/report/`, {
    params: {
      start_date: startDate,
      end_date: endDate,
      // Omitted, the backend compares against the preceding period of the same
      // length, which is what the "Periodo anterior" option means.
      compare_start_date: compareStartDate,
      compare_end_date: compareEndDate,
    },
  });
  return response.data;
};

/**
 * Search Console sites this Google account can read.
 *
 * Same two-level shape as Analytics: the Google account is shared with the
 * agency, and each client points at one verified site of that account.
 */
export const listSearchConsoleSites = async (googleAccountId) => {
  const response = await apiClient.get(`${BASE}/search-console/sites/`, {
    params: { google_account: googleAccountId },
  });
  return response.data.sites;
};

/** The site linked to a client, or null when it has never been connected. */
export const getClientSearchConsoleLink = async (clientId) => {
  const response = await apiClient.get(`${BASE}/search-console/`, {
    params: { client: clientId },
  });
  return response.data[0] || null;
};

/** Link a site to a client, replacing whatever it had. */
export const linkSearchConsoleSite = async (payload) => {
  const response = await apiClient.post(`${BASE}/search-console/`, payload);
  return response.data;
};

export const deleteSearchConsoleSite = async (linkId) => {
  await apiClient.delete(`${BASE}/search-console/${linkId}/`);
};

/** Clicks, impressions, CTR and average position for a linked client. */
export const getSearchConsoleReport = async (
  linkId,
  { startDate, endDate, compareStartDate, compareEndDate } = {}
) => {
  const response = await apiClient.get(
    `${BASE}/search-console/${linkId}/report/`,
    {
      params: {
        start_date: startDate,
        end_date: endDate,
        // Omitted, the backend compares against the preceding period.
        compare_start_date: compareStartDate,
        compare_end_date: compareEndDate,
      },
    }
  );
  return response.data;
};

/** Key events configured in the client's GA4, whether or not they have data. */
export const getConfiguredKeyEvents = async (linkId) => {
  const response = await apiClient.get(`${BASE}/analytics/${linkId}/key-events/`);
  return response.data.key_events;
};
